import { z } from "zod"
import {
  commIntervalEnum,
  journalPriorityEnum,
  journalTypeEnum,
  smsStatusEnum,
} from "../../../db/schema/journal"

export const CreateJournalInput = z.object({
  estateId: z.string().uuid(),
  journalType: z.enum(journalTypeEnum.enumValues),
  commInterval: z.enum(commIntervalEnum.enumValues),
  smsStatus: z.enum(smsStatusEnum.enumValues),
  journalPriority: z.enum(journalPriorityEnum.enumValues),
  name: z.string(),
  content: z.string(),
  description: z.string().nullish(),
  journalNote: z.string().nullish(),
})

export type CreateJournalInput = z.infer<typeof CreateJournalInput>

export const AddResourceToJournalInput = z
  .object({
    journalId: z.string().uuid(),
    assetId: z.string().uuid().optional(),
    taskId: z.string().uuid().optional(),
    contactId: z.string().uuid().optional(),
    documentId: z.string().uuid().optional(),
    companyId: z.string().uuid().optional(),
    trustId: z.string().uuid().optional(),
    insuranceId: z.string().uuid().optional(),
  })
  .refine(
    (data) => {
      const resourceIds = [
        data.assetId,
        data.taskId,
        data.contactId,
        data.documentId,
        data.companyId,
        data.trustId,
        data.insuranceId,
      ].filter(Boolean)
      return resourceIds.length === 1
    },
    { message: "Exactly one resource ID must be provided." },
  )

export type AddResourceToJournalInput = z.infer<typeof AddResourceToJournalInput>

export const RemoveResourceFromJournalInput = z.object({
  journalResourceId: z.string().uuid(),
})

export type RemoveResourceFromJournalInput = z.infer<typeof RemoveResourceFromJournalInput>

export const DeleteJournalInput = z.object({
  journalId: z.string().uuid(),
})

export type DeleteJournalInput = z.infer<typeof DeleteJournalInput>

export const ShareJournalInput = z.object({
  journalId: z.string().uuid(),
  targetUserId: z.string(),
})

export type ShareJournalInput = z.infer<typeof ShareJournalInput>

export const UnshareJournalInput = z.object({
  journalId: z.string().uuid(),
  targetUserId: z.string(),
})

export type UnshareJournalInput = z.infer<typeof UnshareJournalInput>

export const ListSharedWithInput = z.object({
  journalId: z.string().uuid(),
})

export type ListSharedWithInput = z.infer<typeof ListSharedWithInput>

export const ListResourceInput = z.object({
  journalId: z.string().uuid(),
})

export type ListResourceInput = z.infer<typeof ListResourceInput>

export const ListJournalsInput = z.object({
  estateId: z.string().uuid(),
})

export type ListJournalsInput = z.infer<typeof ListJournalsInput>

export const UpdateJournalInput = z.object({
  journalId: z.string().uuid(),
  name: z.string().min(1).optional(),
  content: z.string().optional(),
  description: z.string().nullish(),
  journalNote: z.string().nullish(),
  journalType: z.enum(journalTypeEnum.enumValues).optional(),
  commInterval: z.enum(commIntervalEnum.enumValues).optional(),
})

export type UpdateJournalInput = z.infer<typeof UpdateJournalInput>

export const ListUnResourcedEstateData = z.object({
  estateId: z.string().uuid(),
})

export type ListUnResourcedEstateData = z.infer<typeof ListUnResourcedEstateData>

/** Full entity rows from each source table; keyed arrays for unorganized journal UI. */
const listUnResourcedEntitySchema = z.record(z.string(), z.unknown())

export const ListUnResourcedEstateDataOutput = z.object({
  contacts: z.array(listUnResourcedEntitySchema),
  documents: z.array(listUnResourcedEntitySchema),
  assets: z.array(listUnResourcedEntitySchema),
  companies: z.array(listUnResourcedEntitySchema),
  trusts: z.array(listUnResourcedEntitySchema),
  insurances: z.array(listUnResourcedEntitySchema),
})

export type ListUnResourcedEstateDataOutput = z.infer<typeof ListUnResourcedEstateDataOutput>

export const zJournal = z.object({
  id: z.string().uuid(),
  journalType: z.string(),
  commInterval: z.string().nullable(),
  smsStatus: z.string().nullable(),
  journalPriority: z.string().nullable(),
  createdById: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  journalNote: z.string().nullable(),
  estateId: z.string().uuid(),
  entryDate: z.date(),
  content: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
})
export type ListJournalOutput = z.infer<typeof zJournal>

export const zSharedJournalOutput = z.object({
  id: z.string().uuid(),
  name: z.string(),
  estateId: z.string().uuid(),
  estateName: z.string().nullable(),
  journalType: z.string(),
  journalPriority: z.string().nullable(),
})

export const zListMySharedJournalsOutput = z.array(zSharedJournalOutput)

export type ListMySharedJournalsOutput = z.infer<typeof zListMySharedJournalsOutput>
